import type { AdminUsageDashboardSummary, AdminUsageModelFilterOption } from '@nadoutong/contracts';
import {
  Clapperboard,
  KeyRound,
  Landmark,
  LogIn,
  LogOut,
  RefreshCw,
  Save,
  Users,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AdminUsageDashboardClientQuery } from '../../api/usage.ts';
import { useDisplayRate } from '../../api/useBilling.ts';
import { useAdminUsageDashboard, useUsageUnitTotals } from '../../api/useUsage.ts';
import { formatTokens, formatUsdMicrosOrCentsAsCnyDisplay } from '../../components/formatters.ts';
import { Skeleton } from '../../components/Skeleton.tsx';
import { ExportExcelButton } from '../../export/ExportButton.tsx';
import { isValidExportRate } from '../../export/money.ts';
import { exportUsageSeats } from '../../export/run.ts';
import { useSession } from '../../hooks/useSession.tsx';
import type { I18nKey } from '../../i18n/index.ts';
import { useT } from '../../i18n/index.ts';
import { AdminFilterBar, adminRangePresets } from './AdminFilterBar.tsx';
import type { TokenMetric } from './AdminPanels.tsx';
import {
  DepartmentMixPanel,
  MetricToggle,
  ModelMixPanel,
  ServiceHealthPanel,
  StackedTrendChart,
} from './AdminPanels.tsx';
import type { ModelDimension } from './calibers.ts';
import { isModelOptionFor, seatKeyCalibers } from './calibers.ts';
import {
  dayRangeToIsoWindow,
  formatCompactCount,
  formatIsoDate,
  formatShortTime,
  formatUnitQuantities,
  resolveRangeSelection,
  TREND_BUCKET_TIMEZONE,
  todayLocalDay,
  uncachedInput,
} from './format.ts';
import { SeatUsageTable } from './SeatUsageTable.tsx';
import { MAX_RANGE_SPAN_DAYS, PanelHeader, SummaryCard, UsageError } from './shared.tsx';

/** Preset applied when `?range=` is absent or unrecognisable. */
const DEFAULT_RANGE = '30d';

/**
 * URL params are user-editable, so both readers narrow rather than
 * trust: anything unrecognised collapses to the default instead of
 * reaching the API as a value it would reject.
 */
function parseGranularity(value: string | null): 'day' | 'week' | 'month' {
  return value === 'week' || value === 'month' ? value : 'day';
}

function parseGroupBy(value: string | null): 'model' | 'department' | 'member' {
  return value === 'department' || value === 'member' ? value : 'model';
}

/**
 * The trend subtitle reads "Stacked by <dimension>", and the dimension
 * is a bare API token (`model` / `department` / `member`). Chinese
 * reorders that sentence entirely, so the dimension is looked up as its
 * own noun and substituted, never concatenated.
 */
const GROUP_BY_VALUE_KEYS: Readonly<Record<'model' | 'department' | 'member', I18nKey>> = {
  model: 'usage.admin.group_by_value.model',
  department: 'usage.admin.group_by_value.department',
  member: 'usage.admin.group_by_value.member',
};

/**
 * Org-wide `/usage` dashboard, reached when the session holds
 * `org:usage:read`. This file is the composition root of the admin
 * branch: it owns ALL query state and the page layout, and delegates
 * every visual block to the sibling modules.
 *
 * State lives here for one reason — the whole screen is driven by a
 * single `AdminUsageDashboardClientQuery`, and the offset/filter
 * interaction is a page-level rule: any filter change resets seat
 * paging to 0, or the operator lands on page 4 of a 1-page result.
 * Pushing that rule into the filter bar or the table would duplicate
 * it in two places that cannot see each other.
 *
 * Empty-string filter values are the "all" sentinel and are dropped
 * from the query rather than sent as wildcards; the server decides
 * what unfiltered means.
 *
 * The nine filter dimensions live in the URL, not in component state,
 * so a dashboard view is a link an operator can paste into a ticket.
 * Writes go through `patchParams` with `replace`, because dragging a
 * date range should not bury the previous page under twenty history
 * entries. Everything that is a view preference rather than a filter
 * (seat search, paging offset, chart metric) stays local — it does not
 * describe the data, only how it is being looked at right now.
 *
 * Excel export reuses the exact same filter object as the on-screen
 * view (then walks seat pagination from offset 0), so an export can
 * never describe a different window than the one being looked at.
 */
export function AdminUsage(): JSX.Element {
  const t = useT();
  const { user, fetchImpl } = useSession();
  const displayRateQ = useDisplayRate();
  const [searchParams, setSearchParams] = useSearchParams();
  const today = todayLocalDay();
  const rangePresets = useMemo(() => adminRangePresets(today, t), [today, t]);
  const rangeSelection = useMemo(
    () =>
      resolveRangeSelection({
        rangeParam: searchParams.get('range'),
        fromParam: searchParams.get('from'),
        toParam: searchParams.get('to'),
        presets: rangePresets,
        defaultPresetId: DEFAULT_RANGE,
        maxSpanDays: MAX_RANGE_SPAN_DAYS,
        today,
      }),
    [searchParams, rangePresets, today],
  );
  const granularity = parseGranularity(searchParams.get('granularity'));
  const groupBy = parseGroupBy(searchParams.get('groupBy'));
  const model = searchParams.get('model') ?? '';
  // #1672: the model dimension is keyed by vendor + model, so restoring a
  // selection from a link takes both. An empty `vendor` beside a `model`
  // is not "missing" — it is the union across every vendor serving that
  // name, which is what a link made before #1672 carries.
  const vendor = searchParams.get('vendor') ?? '';
  const apiKeyId = searchParams.get('apiKeyId') ?? '';
  const department = searchParams.get('department') ?? '';
  const memberAccountId = searchParams.get('member') ?? '';

  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [metric, setMetric] = useState<TokenMetric>('total');

  /**
   * Single write path for the URL filters. A `null` or empty value
   * removes the param so an "all" selection leaves no trace, and every
   * write resets paging — a filter change reshapes the result set, and
   * page 4 of the old one means nothing in the new one.
   */
  function patchParams(patch: Readonly<Record<string, string | null>>): void {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value.length === 0) next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
    setOffset(0);
  }

  const queryRange = useMemo(
    () => dayRangeToIsoWindow(rangeSelection.range),
    [rangeSelection.range],
  );
  const query = useMemo<AdminUsageDashboardClientQuery>(
    () => ({
      ...queryRange,
      // #1678: the server cuts trend buckets in whatever `tz` the caller
      // declares (default: the client sends the browser zone). This page
      // PINS the zone its own axis math is written in: `trendBucketAxis`
      // materializes UTC-cut keys and `format.ts` documents that flipping
      // `TREND_BUCKET_TIMEZONE` requires parameterizing that math at the
      // same time — until then, a browser-zone `tz` here would make the
      // server return locally-cut keys the axis never predicts (a phantom
      // empty leading bucket, wrongly-hatched edges, and a bucket-timezone
      // caption that lies).
      tz: TREND_BUCKET_TIMEZONE,
      granularity,
      groupBy,
      model: model.length > 0 ? model : undefined,
      // Only ever alongside a model: a lone `vendor` is ignored by the
      // server, so sending one would put a filter in the URL that the
      // dashboard does not honour.
      vendor: model.length > 0 && vendor.length > 0 ? vendor : undefined,
      apiKeyId: apiKeyId.length > 0 ? apiKeyId : undefined,
      department: department.length > 0 ? department : undefined,
      memberAccountId: memberAccountId.length > 0 ? memberAccountId : undefined,
      q: search.length > 0 ? search : undefined,
      limit: 12,
      offset,
    }),
    [
      apiKeyId,
      department,
      granularity,
      groupBy,
      memberAccountId,
      model,
      offset,
      queryRange,
      search,
      vendor,
    ],
  );
  const dashboardQ = useAdminUsageDashboard(query, true);
  // Same query, separate request. A slow or failed unit-totals read must
  // not hold the token dashboard: the card below has its own loading and
  // error text, and the model mix treats a missing payload as "no units".
  const unitTotalsQ = useUsageUnitTotals(query, true);
  const dashboard = dashboardQ.data;
  const unitBillingValue = unitTotalsQ.isError
    ? '—'
    : unitTotalsQ.data === undefined
      ? t('usage.summary.loading')
      : formatUnitQuantities(unitTotalsQ.data.totals, (unit) =>
          t(`requests.unit_billing.unit.${unit}`),
        ) || '0';
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;

  const modelSelection = useMemo<ModelDimension>(() => ({ model, vendor }), [model, vendor]);
  /**
   * The dropdown's rows, plus — only when needed — one synthetic row for
   * a selection the server did not offer.
   *
   * A link made before #1672 carries a bare `model` and no `vendor`, and
   * that still filters the dashboard: it selects every vendor serving
   * that name. The server's option list has no such row (each of its
   * rows names one vendor), so without this the control would read "all
   * models" over a dashboard that is filtered to one — the same lie the
   * unknown-API-key row exists to prevent.
   *
   * A vendor-qualified selection the server did not list gets no such
   * row: its label is the server's to compose (`model · vendor`), and a
   * second place composing it would be a second place to drift.
   */
  const modelOptions = useMemo<readonly AdminUsageModelFilterOption[]>(() => {
    const options = dashboard?.filters.models ?? [];
    if (model.length === 0 || vendor.length > 0) return options;
    if (options.some((option) => isModelOptionFor(option, modelSelection))) return options;
    return [
      { id: model, label: t('usage.admin.filter.model_all_vendors', { model }), vendor: '', model },
      ...options,
    ];
  }, [dashboard?.filters.models, model, modelSelection, t, vendor]);

  return (
    <section data-testid="usage-page" className="flex flex-col gap-5">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex max-w-3xl flex-col gap-2">
          <h2 className="font-display text-display text-ink">{t('usage.admin.title')}</h2>
          <p className="text-body leading-6 text-ink-mid">{t('usage.admin.subtitle')}</p>
        </div>
        {user !== null ? (
          <ExportExcelButton
            testId="usage-export"
            disabled={!isValidExportRate(cnyPerUsd)}
            onExport={() =>
              exportUsageSeats({
                fetchImpl,
                orgId: user.orgId,
                query,
                rangeDays: rangeSelection.range,
                cnyPerUsd,
              })
            }
          />
        ) : null}
      </header>

      <AdminFilterBar
        dashboard={dashboard}
        range={rangeSelection.range}
        rangePresets={rangePresets}
        activePresetId={rangeSelection.presetId}
        granularity={granularity}
        groupBy={groupBy}
        modelOptions={modelOptions}
        modelSelection={modelSelection}
        apiKeyId={apiKeyId}
        department={department}
        memberAccountId={memberAccountId}
        onRange={(next, presetId) => {
          if (presetId !== null) patchParams({ range: presetId, from: null, to: null });
          else patchParams({ range: 'custom', from: next.from, to: next.to });
        }}
        onGranularity={(next) => patchParams({ granularity: next })}
        onGroupBy={(next) => patchParams({ groupBy: parseGroupBy(next) })}
        // Both halves of the dimension are written together, so the URL
        // can never hold a vendor belonging to a different model.
        onModel={(next) =>
          patchParams({ model: next?.model ?? null, vendor: next?.vendor ?? null })
        }
        onApiKey={(next) => patchParams({ apiKeyId: next })}
        onDepartment={(next) => patchParams({ department: next })}
        onMember={(next) => patchParams({ member: next })}
      />

      {dashboardQ.isLoading ? (
        <AdminDashboardSkeleton />
      ) : dashboardQ.isError ? (
        <UsageError
          onRetry={() => {
            void dashboardQ.refetch();
          }}
          testId="usage-admin-error"
        />
      ) : dashboard !== undefined ? (
        <>
          {/*
            The window the numbers below describe, stated ONCE. Panel
            subtitles say what they stack, not when — a second copy of
            the dates is a second thing to keep in sync.
          */}
          <div
            className="flex flex-col items-end gap-1 rounded-lg border border-line bg-raised px-4 py-3 text-body text-ink-low sm:flex-row sm:items-center sm:justify-between"
            data-testid="usage-window-bar"
          >
            {/*
              The dates above render in the READER's zone while the
              trend buckets below are cut in the server's, and the two
              are indistinguishable as bare `YYYY-MM-DD` strings. Saying
              which zone the buckets use is the only thing that makes
              the chart readable until the server takes a timezone.
            */}
            <span className="text-caption" data-testid="usage-window-bucket-timezone">
              {t('usage.admin.window.bucket_timezone', { timezone: TREND_BUCKET_TIMEZONE })}
            </span>
            <span className="font-mono">
              {t('usage.admin.window.meta', {
                from: formatIsoDate(dashboard.window.from),
                to: formatIsoDate(dashboard.window.to),
                time: formatShortTime(dashboard.refreshedAt),
              })}
            </span>
          </div>

          {/*
            Eight tiles, two full rows at xl. The seat/key pair stays
            together — each pairs one window figure with its roster
            counterpart — and the unit-billing tile closes the second
            row. Four-up rather than a wider row: at six, the roster
            caption wrapped to three lines.
          */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryCard
              label={t('usage.summary.tokens_in')}
              value={t('usage.summary.tokens_value', {
                // Uncached input. `totalInputTokens` already includes cache
                // reads on the OpenAI lane, so printing it beside the cache
                // tiles counts those tokens twice.
                tokens: formatCompactCount(
                  uncachedInput({
                    cacheEligibleInputTokens: dashboard.summary.cacheEligibleInputTokens,
                    cacheReadTokens: dashboard.summary.totalCacheReadTokens,
                    cacheWriteTokens: dashboard.summary.totalCacheWriteTokens,
                  }),
                ),
              })}
              detail={t('usage.requests_count', {
                count: formatTokens(dashboard.summary.totalRequests),
              })}
              icon={LogIn}
              testId="usage-summary-input-tokens"
            />
            <SummaryCard
              label={t('usage.summary.tokens_out')}
              value={t('usage.summary.tokens_value', {
                tokens: formatCompactCount(dashboard.summary.totalOutputTokens),
              })}
              detail={t('usage.succeeded_count', {
                count: formatTokens(dashboard.summary.successRequests),
              })}
              icon={LogOut}
              testId="usage-summary-output-tokens"
            />
            <SummaryCard
              label={t('usage.summary.cache_write')}
              value={t('usage.summary.tokens_value', {
                tokens: formatCompactCount(dashboard.summary.totalCacheWriteTokens),
              })}
              detail={t('usage.admin.summary.cache_write_detail')}
              icon={Save}
              testId="usage-summary-cache-write-tokens"
            />
            <SummaryCard
              label={t('usage.summary.cache_read')}
              value={t('usage.summary.tokens_value', {
                tokens: formatCompactCount(dashboard.summary.totalCacheReadTokens),
              })}
              detail={t('usage.admin.summary.cache_read_detail')}
              icon={RefreshCw}
              testId="usage-summary-cache-read-tokens"
            />
            <SummaryCard
              label={t('usage.summary.blended_spend')}
              value={formatUsdMicrosOrCentsAsCnyDisplay(
                dashboard.summary.totalCostUsdMicros,
                dashboard.summary.totalCostUsdCents,
                cnyPerUsd,
              )}
              detail={t('usage.admin.summary.cost_detail')}
              icon={Landmark}
              testId="usage-summary-cost"
            />
            <SeatKeyCards summary={dashboard.summary} />
            {/*
              `truncated` means the scan dropped rows. The quantities that
              arrived are still the value; there is no extra caption.
            */}
            <SummaryCard
              label={t('requests.detail.unit_billing')}
              value={unitBillingValue}
              detail={t('usage.admin.summary.unit_billing_detail')}
              icon={Clapperboard}
              testId="usage-summary-unit-billing"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]">
            <article
              data-testid="usage-admin-trend-card"
              className="flex flex-col gap-4 rounded-lg border border-line bg-card p-5"
            >
              <PanelHeader
                title={t('usage.admin.trend.title')}
                subtitle={t('usage.admin.trend.subtitle', {
                  groupBy: t(GROUP_BY_VALUE_KEYS[dashboard.window.groupBy]),
                })}
                action={<MetricToggle metric={metric} onChange={setMetric} />}
              />
              <StackedTrendChart
                points={dashboard.trend}
                metric={metric}
                window={{
                  from: dashboard.window.from,
                  to: dashboard.window.to,
                  // Vendored contracts added `hour` to AdminUsageGranularity.
                  // This page never requests it (`parseGranularity`), and
                  // the trend axis has no hour grain, so that echo — if it
                  // ever arrived — is read on the day axis instead.
                  granularity:
                    dashboard.window.granularity === 'hour' ? 'day' : dashboard.window.granularity,
                }}
              />
              <div className="flex items-center justify-between rounded-lg border border-line bg-raised px-3 py-2 text-caption text-ink-low">
                <span>{t('usage.admin.trend.hint')}</span>
                <span className="font-mono text-ink">
                  {t('usage.admin.trend.total', {
                    // Eligible input already includes cache read and write,
                    // matching the cache-inclusive total the model mix prints.
                    tokens: formatCompactCount(
                      dashboard.summary.cacheEligibleInputTokens +
                        dashboard.summary.totalOutputTokens,
                    ),
                  })}
                </span>
              </div>
            </article>

            <ModelMixPanel
              models={dashboard.models}
              unitModels={unitTotalsQ.isSuccess ? unitTotalsQ.data?.models : undefined}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <DepartmentMixPanel departments={dashboard.departments} />
            <ServiceHealthPanel dashboard={dashboard} />
          </div>

          <SeatUsageTable
            dashboard={dashboard}
            cnyPerUsd={cnyPerUsd}
            search={search}
            onSearch={(next) => {
              setSearch(next);
              setOffset(0);
            }}
            onOffset={setOffset}
          />
        </>
      ) : null}
    </section>
  );
}

/**
 * The last two tiles of the summary grid: how many seats and keys moved
 * tokens inside the selected window, each captioned with the roster it
 * is NOT a fraction of.
 *
 * The pairing is the whole point. `seatsWithUsage` counts a population
 * the window defines; `totalSeats` counts the one the roster defines
 * right now, and a member who spent tokens and then left belongs to the
 * first and not the second — so `seatsWithUsage` may exceed
 * `totalSeats`, and any "N of M" rendering would present that ordinary
 * fact as an impossible fraction. Two tiles, each one headline number
 * over one differently-labelled caption, make that shape unavailable:
 * there is no slot a ratio could be written into.
 *
 * The keys tile exists for the same reason one number cannot carry two
 * calibers — `activeApiKeys` (not revoked, right now) and
 * `keysWithUsage` (had traffic in the window, revoked since or not) are
 * different questions, and the single "active keys" line this replaced
 * could be read as either.
 */
function SeatKeyCards({ summary }: { readonly summary: AdminUsageDashboardSummary }): JSX.Element {
  const t = useT();
  const calibers = seatKeyCalibers(summary);
  return (
    <>
      <SummaryCard
        label={t('usage.admin.summary.seats_with_usage')}
        value={formatTokens(calibers.seatsWithUsage)}
        detail={t('usage.admin.summary.seats_roster_detail', {
          seats: formatTokens(calibers.seatsOnRoster),
        })}
        icon={Users}
        testId="usage-summary-seats-with-usage"
      />
      <SummaryCard
        label={t('usage.admin.summary.keys_with_usage')}
        value={formatTokens(calibers.keysWithUsage)}
        detail={
          calibers.keysOnRoster === null
            ? t('usage.admin.summary.keys_roster_unknown')
            : t('usage.admin.summary.keys_roster_detail', {
                keys: formatTokens(calibers.keysOnRoster),
              })
        }
        icon={KeyRound}
        testId="usage-summary-keys-with-usage"
      />
    </>
  );
}

/** Loading placeholder shaped like the dashboard it replaces, so the page does not jump on arrival. */
function AdminDashboardSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-4" data-testid="usage-admin-loading">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3, 4, 5, 6, 7].map((item) => (
          <Skeleton key={item} className="h-32" />
        ))}
      </div>
      <Skeleton className="h-80" />
      <Skeleton className="h-96" />
    </div>
  );
}
